/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useBrandingData.ts
 🗓️ Created: 2025-01-XX
 🧩 Description:
    Hook for loading branding data for edit mode.
────────────────────────────────────────────────────────────────*/

import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useI18n } from '../../../hooks/useI18n';
import { useAuthStore } from '../../../store/auth';
import { fetchTenantBranding } from '../api/brandingApi';
import type { BrandingConfig } from '../types';

const DEFAULT_BRANDING: BrandingConfig = {
  logo: null,
  logoDark: null,
  mobileLogo: null,
  mobileLogoDark: null,
  favicon: null,
  faviconDark: null,
  primaryColor: '#3B82F6',
  secondaryColor: '#64748B',
  appName: 'Ring2All Platform',
  appDescription: 'Multi-tenant communication platform',
  welcomeMessage: 'Welcome to Ring2All Platform',
  footerText: '© 2026 Ring2All LLC. All rights reserved.',
  loginBackgroundColor: '#ffffff',
  loginFontColor: '#000000',
  loginFormBackground: '#4448476B',
  loginSubmitButtonColor: '#202C2C7D',
  loginAddonsMenuColor: '#24363656',
  loginBackgroundImage: null,
  loginLogo: null,
  disableLoginBackgroundImage: false,
};

export function useBrandingData(tenantId: string | undefined) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { user } = useAuthStore();
  const tenantSlug = user?.tenant?.slug || 'main';
  const brandingLoadedRef = useRef<string | null>(null);

  const [tenant, setTenant] = useState<{
    id: number;
    name: string;
    slug: string;
  } | null>(null);
  const [brandingConfig, setBrandingConfig] =
    useState<BrandingConfig>(DEFAULT_BRANDING);
  const [loading, setLoading] = useState(true);

  const loadBranding = useCallback(async () => {
    if (!tenantId) {
      showError(t('branding.errors.tenantNotFound'));
      navigate('/admin/system-settings/branding');
      return;
    }

    if ((location.state as any)?.fromBranding) {
      return;
    }

    // Prevent duplicate API calls
    if (brandingLoadedRef.current === tenantId) {
      return;
    }
    brandingLoadedRef.current = tenantId;

    setLoading(true);
    try {
      const result = await fetchTenantBranding(
        tenantId,
        tenantSlug,
        user?.id ? Number(user.id) : undefined
      );

      const config: BrandingConfig = {
        ...DEFAULT_BRANDING,
        ...result.brandingConfig,
      };

      setTenant({
        id: result.tenant.id,
        name: result.tenant.name,
        slug: result.tenant.slug,
      });
      setBrandingConfig(config);
    } catch (error) {
      if ((error as Error).message === 'TENANT_NOT_FOUND') {
        showError(t('branding.errors.tenantNotFound'));
        navigate('/admin/system-settings/branding');
      } else {
        showError(t('branding.errors.loadFailed'));
      }
    } finally {
      setLoading(false);
    }
  }, [tenantId, tenantSlug, user?.id, navigate, showError, t, location.state]);

  useEffect(() => {
    loadBranding();
  }, [loadBranding]);

  return {
    tenant,
    brandingConfig,
    loading,
    reload: loadBranding,
  };
}

